/**
 * Money is integer paise. Always. Everywhere.
 *
 * ERD convention 0.5: there is no REAL money column anywhere in this model, and
 * no floating point arithmetic on money anywhere in this codebase. The system
 * being replaced stores wallet_transactions.amount as double precision, and that
 * is a listed trap. Everything here works in integers only.
 */

/** Paise. A branded integer so a rupee number cannot be passed where paise are wanted. */
export type Paise = number & { readonly __brand: 'paise' }

export class MoneyError extends Error {}

/** Assert a value is a safe integer number of paise. */
export function paise(value: number): Paise {
  if (!Number.isInteger(value)) {
    throw new MoneyError(`money must be an integer number of paise, got ${value}`)
  }
  if (!Number.isSafeInteger(value)) {
    throw new MoneyError(`money out of safe integer range: ${value}`)
  }
  return value as Paise
}

/** Rupees (may be fractional to two places) to paise, rounded half away from zero. */
export function rupeesToPaise(rupees: number): Paise {
  if (!Number.isFinite(rupees)) throw new MoneyError(`not a finite amount: ${rupees}`)
  const scaled = rupees * 100
  const rounded = scaled < 0 ? -Math.round(-scaled) : Math.round(scaled)
  return paise(rounded)
}

/** Display only. Never feed the result back into arithmetic. */
export function formatPaise(amount: Paise | number, opts: { symbol?: boolean } = {}): string {
  const v = paise(amount as number)
  const negative = v < 0
  const abs = Math.abs(v)
  const whole = Math.floor(abs / 100)
  const frac = abs % 100
  // Indian grouping: last three digits, then pairs.
  const s = String(whole)
  const head = s.length > 3 ? s.slice(0, s.length - 3) : ''
  const tail = s.slice(-3)
  const grouped = head ? `${head.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${tail}` : tail
  const body = `${grouped}.${String(frac).padStart(2, '0')}`
  return `${negative ? '-' : ''}${opts.symbol === false ? '' : 'Rs '}${body}`
}

export function addPaise(...amounts: Array<Paise | number>): Paise {
  return paise(amounts.reduce<number>((sum, a) => sum + paise(a as number), 0))
}

export function subtractPaise(a: Paise | number, b: Paise | number): Paise {
  return paise(paise(a as number) - paise(b as number))
}

export function multiplyPaise(amount: Paise | number, qty: number): Paise {
  if (!Number.isInteger(qty)) throw new MoneyError(`quantity must be an integer, got ${qty}`)
  return paise(paise(amount as number) * qty)
}

/**
 * Apply a basis-points multiplier (10000 bp = 1x). Integer arithmetic throughout,
 * rounded half away from zero. Condition-grade multipliers and pricing rules both
 * use basis points precisely so no float enters pricing.
 */
export function applyBasisPoints(amount: Paise | number, bp: number): Paise {
  if (!Number.isInteger(bp)) throw new MoneyError(`basis points must be an integer, got ${bp}`)
  const product = paise(amount as number) * bp
  const negative = product < 0
  const abs = Math.abs(product)
  const rounded = Math.floor((abs + 5000) / 10000)
  return paise(negative ? -rounded : rounded)
}

/** Round to the nearest step, e.g. roundToPaise(x, 5000) rounds to the nearest Rs 50. */
export function roundToPaise(amount: Paise | number, step: number): Paise {
  if (!Number.isInteger(step) || step <= 0) {
    throw new MoneyError(`round step must be a positive integer, got ${step}`)
  }
  const v = paise(amount as number)
  const negative = v < 0
  const abs = Math.abs(v)
  const rounded = Math.floor((abs + Math.floor(step / 2)) / step) * step
  return paise(negative ? -rounded : rounded)
}

/**
 * MNY-08. The pricing path's basis-point multiply, floored.
 *
 * Distinct from applyBasisPoints, which rounds half away from zero and is the
 * right primitive for reconciliation arithmetic. A suggested price must never
 * round *up* past what the rule intended, so the two are separate functions
 * rather than a mode flag on one - a caller that picks the wrong mode by
 * accident produces a price nobody agreed to.
 */
export function priceFromBasisPoints(referencePricePaise: Paise | number, bp: number): Paise {
  if (!Number.isInteger(bp)) throw new MoneyError(`basis points must be an integer, got ${bp}`)
  if (bp < 0) throw new MoneyError(`basis points must be non-negative, got ${bp}`)
  const reference = paise(referencePricePaise as number)
  return paise(Math.floor((reference * bp) / 10000))
}

export interface SuggestPriceInput {
  referencePricePaise: number
  multiplierBp: number
  /** MNY-09: rounding is explicit and to paise. Absent means no rounding step. */
  roundToPaise?: number
  ruleId: string
}

export interface PriceSuggestion {
  suggestedPricePaise: Paise
  rawPricePaise: Paise
  /** The rule that fired, so an override is measurable against it. */
  pricingRuleId: string
}

/**
 * CAT-14. Price is suggested, never decided here: the caller may override, and
 * the original is kept on the listing so the override rate stays measurable.
 */
export function suggestPrice(input: SuggestPriceInput): PriceSuggestion {
  const raw = priceFromBasisPoints(input.referencePricePaise, input.multiplierBp)
  const suggested = input.roundToPaise ? roundToPaise(raw, input.roundToPaise) : raw
  return {
    suggestedPricePaise: suggested,
    rawPricePaise: raw,
    pricingRuleId: input.ruleId,
  }
}

/**
 * Split an amount across n weighted shares with no paise lost.
 * Used for allocating one carrier cost across the orders in a consolidated
 * ships-together household parcel, pro rata by shipping_collected_paise, with
 * the remainder paise going to the earliest-placed order (index 0 by contract).
 */
export function allocateByWeight(amount: Paise | number, weights: number[]): Paise[] {
  const total = paise(amount as number)
  if (weights.length === 0) throw new MoneyError('cannot allocate across zero shares')
  if (weights.some((w) => !Number.isInteger(w) || w < 0)) {
    throw new MoneyError('weights must be non-negative integers')
  }
  const weightSum = weights.reduce((a, b) => a + b, 0)
  if (weightSum === 0) {
    // Degenerate case: split as evenly as integers allow.
    const base = Math.floor(total / weights.length)
    const out = weights.map(() => base)
    let remainder = total - base * weights.length
    for (let i = 0; remainder > 0; i = (i + 1) % out.length, remainder--) out[i]! += 1
    return out.map((v) => paise(v))
  }
  const out = weights.map((w) => Math.floor((total * w) / weightSum))
  const allocated = out.reduce((a, b) => a + b, 0)
  let remainder = total - allocated
  for (let i = 0; remainder > 0; i = (i + 1) % out.length, remainder--) out[i]! += 1
  return out.map((v) => paise(v))
}

/**
 * Order arithmetic, in one place, so a total is never recomputed client-side.
 * The old system stores cart aggregates recomputed in the browser, and that is
 * the direct cause of its price bugs.
 */
export interface OrderTotalsInput {
  itemsTotalPaise: number
  shippingCollectedPaise: number
  packagingPaise: number
  discountPaise: number
  creditAppliedPaise: number
}

export function grandTotalPaise(input: OrderTotalsInput): Paise {
  return subtractPaise(
    addPaise(input.itemsTotalPaise, input.shippingCollectedPaise, input.packagingPaise),
    addPaise(input.discountPaise, input.creditAppliedPaise),
  )
}

/**
 * The shipping credit mechanic: a flat amount is collected toward shipping on
 * every payment, and the difference against what the carrier actually charged
 * becomes a credit ledger entry rather than a per-shipment collection round trip.
 */
export type ShippingReconciliation =
  | { direction: 'credit'; reason: 'shipping_overcollection'; amountPaise: Paise }
  | { direction: 'debit'; reason: 'shipping_shortfall'; amountPaise: Paise }
  | null

export function reconcileShipping(collectedPaise: number, actualPaise: number): ShippingReconciliation {
  const delta = subtractPaise(collectedPaise, actualPaise)
  if (delta === 0) return null
  return delta > 0
    ? { direction: 'credit', reason: 'shipping_overcollection', amountPaise: paise(delta) }
    : { direction: 'debit', reason: 'shipping_shortfall', amountPaise: paise(-delta) }
}
